import { alpha, type Theme } from "@mui/material/styles";

/** Card / accordion radius; MuiAccordion sets `last-of-type` / `first-of-type` from `theme.shape`, so shells override those. */
export const SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX = "8px";

/** Dense accordion summary content (shared with resource book dialogs). */
export const scheduleResourceBookAccordionDenseSummarySx = {
  "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": {
    margin: "4px 0"
  }
} as const;

/** Availability-style surface: primary gradient + primary left accent (resource book date step `Paper`). */
export function scheduleResourceBookPrimarySurfaceSx(muiTheme: Theme) {
  return {
    overflow: "hidden",
    borderRadius: SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "divider",
    borderLeftColor: muiTheme.palette.primary.main,
    borderLeftWidth: 4,
    background: `linear-gradient(135deg, ${alpha(muiTheme.palette.primary.main, 0.07)} 0%, ${alpha(muiTheme.palette.primary.main, 0.02)} 45%, ${muiTheme.palette.background.paper} 100%)`,
    "&:before": { display: "none" }
  };
}

/** Booking-range-style surface: subtle fill + secondary left accent (resource book date step `Paper`). */
export function scheduleResourceBookNeutralSurfaceSx(muiTheme: Theme) {
  const isDark = muiTheme.palette.mode === "dark";
  return {
    overflow: "hidden",
    borderRadius: SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "divider",
    borderLeftColor: muiTheme.palette.text.secondary,
    borderLeftWidth: 4,
    bgcolor: alpha(muiTheme.palette.text.primary, isDark ? 0.06 : 0.03),
    "&:before": { display: "none" }
  };
}

/** Accordion wrapper: border + left accent only; background stays transparent so details stay clear. */
export function scheduleResourceBookAccordionShellPrimarySx(muiTheme: Theme) {
  const r = SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX;
  return {
    overflow: "hidden",
    borderRadius: r,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "divider",
    borderLeftColor: muiTheme.palette.primary.main,
    borderLeftWidth: 4,
    bgcolor: "transparent",
    // MuiAccordion (Paper) uses `borderRadius: 0` and only rounds corners on `:first-of-type` / `:last-of-type` via `theme.shape`.
    "&:first-of-type": {
      borderTopLeftRadius: r,
      borderTopRightRadius: r
    },
    "&:last-of-type": {
      borderBottomLeftRadius: r,
      borderBottomRightRadius: r
    },
    "&:before": { display: "none" }
  };
}

export function scheduleResourceBookAccordionShellNeutralSx(muiTheme: Theme) {
  const r = SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX;
  return {
    overflow: "hidden",
    borderRadius: r,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "divider",
    borderLeftColor: muiTheme.palette.text.secondary,
    borderLeftWidth: 4,
    bgcolor: "transparent",
    "&:first-of-type": {
      borderTopLeftRadius: r,
      borderTopRightRadius: r
    },
    "&:last-of-type": {
      borderBottomLeftRadius: r,
      borderBottomRightRadius: r
    },
    "&:before": { display: "none" }
  };
}

/** Invitation accordion summary: primary gradient (matches availability `Paper` header look). */
export const scheduleResourceBookAccordionSummaryPrimarySx = [
  scheduleResourceBookAccordionDenseSummarySx,
  (theme: Theme) => ({
    px: { sm: 2.5, xs: 2 },
    minHeight: 48,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 45%, ${theme.palette.background.paper} 100%)`,
    "&:hover": {
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 45%, ${theme.palette.background.paper} 100%)`
    }
  })
];

/** Services accordion summary: neutral tint (matches booking-range `Paper`). */
export const scheduleResourceBookAccordionSummaryNeutralSx = [
  scheduleResourceBookAccordionDenseSummarySx,
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      px: { sm: 1.5, xs: 1.25 },
      minHeight: 48,
      bgcolor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.03),
      "&:hover": {
        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.09 : 0.05)
      }
    };
  }
];
